import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Typography } from 'antd';
import { CopyOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import CollapseButton from '../common/buttons/CollapseButton';
import BorderlessButton from '../common/buttons/BorderlessButton';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import SimpleTextInputModal from '../common/modals/SimpleTextInputModal';
import Colors from '../constants/Colors';

const { Text } = Typography;

export default function SectionDisplayHeader({
  name,
  sectionIndex,
  sectionId,
  defaultCollapsed,
  isDuplicationEnabled,
  isCheckAllYesNoEnabled,
  isRenamingEnabled,
  duplicatedParentId,
  collapsed,
  onCollapsedChanged,
  copySection,
  onDeleteClicked,
  checkAllYesNo,
  onSectionRenamed,
}) {
  const isRenameActive = isRenamingEnabled && (!!duplicatedParentId || isDuplicationEnabled);
  const [draft, setDraft] = useState(name);
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);

  const commit = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== name) {
      onSectionRenamed?.(sectionId, trimmed);
    } else {
      setDraft(name);
    }
  }, [draft, name, sectionId, onSectionRenamed]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter') e.target.blur();
    if (e.key === 'Escape') { setDraft(name); e.target.blur(); }
  }, [name]);

  const onCopyClicked = useCallback(() => {
    if (isRenamingEnabled) {
      setDuplicateModalVisible(true);
      return;
    }
    copySection(sectionId)();
  }, [isRenamingEnabled, sectionId, copySection]);

  const onDuplicateConfirm = useCallback((newName) => {
    setDuplicateModalVisible(false);
    copySection(sectionId, newName.trim() || name)();
  }, [sectionId, name, copySection]);

  const onDuplicateCancel = useCallback(() => {
    setDuplicateModalVisible(false);
  }, []);

  return (
    <>
      <Row
        align="middle"
        className="section-display-header"
      >
        <Col>
          {
            defaultCollapsed
            && (
              <CollapseButton
                open={!collapsed[sectionIndex]}
                onClick={onCollapsedChanged(sectionIndex)}
              />
            )
          }
        </Col>
        <Col
          style={{
            minHeight: 25,
            marginLeft: defaultCollapsed ? 10 : 0,
          }}
          flex="calc(100% - 200px)"
        >
          {isRenameActive ? (
            <OnTraccrTextInput
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={onKeyDown}
              bordered={false}
              className="section-display-header-input"
            />
          ) : (
            <Text className="section-display-header-text">
              {name}
            </Text>
          )}
        </Col>
        <Col style={{ paddingLeft: 20 }}>
          <Row justify="end" align="middle" style={{ height: 29 }} gutter={10}>
            <Col>
              { isDuplicationEnabled && (
                <BorderlessButton
                  style={{ padding: 0, background: 'inherit', color: Colors.REDESIGN_PRIMARY_TEXT }}
                  iconNode={<CopyOutlined />}
                  onClick={onCopyClicked}
                />
              )}
            </Col>
            <Col>
              { isCheckAllYesNoEnabled && (
                <BorderlessButton
                  style={{ padding: 0, background: 'inherit', color: Colors.REDESIGN_PRIMARY_TEXT }}
                  iconNode={<CheckCircleOutlined />}
                  onClick={checkAllYesNo(sectionId)}
                />
              )}
            </Col>
            <Col>
              { duplicatedParentId && (
                <BorderlessButton
                  style={{ padding: 0, background: 'inherit' }}
                  iconNode={<DeleteOutlined style={{ color: Colors.FADED_ONTRACCR_RED }} />}
                  onClick={onDeleteClicked(sectionId)}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <SimpleTextInputModal
        title="Name Duplicated Section"
        visible={duplicateModalVisible}
        initialValue={name}
        onClose={onDuplicateCancel}
        onSave={onDuplicateConfirm}
        saveText="Duplicate"
      />
    </>
  );
}

SectionDisplayHeader.propTypes = {
  name: PropTypes.string.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  sectionId: PropTypes.string.isRequired,
  defaultCollapsed: PropTypes.bool,
  isDuplicationEnabled: PropTypes.bool,
  isCheckAllYesNoEnabled: PropTypes.bool,
  isRenamingEnabled: PropTypes.bool,
  duplicatedParentId: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  collapsed: PropTypes.object.isRequired,
  onCollapsedChanged: PropTypes.func.isRequired,
  copySection: PropTypes.func.isRequired,
  onDeleteClicked: PropTypes.func.isRequired,
  checkAllYesNo: PropTypes.func,
  onSectionRenamed: PropTypes.func,
};

SectionDisplayHeader.defaultProps = {
  defaultCollapsed: false,
  isDuplicationEnabled: false,
  isCheckAllYesNoEnabled: false,
  isRenamingEnabled: false,
  duplicatedParentId: null,
  checkAllYesNo: null,
  onSectionRenamed: null,
};
